import {noop} from '@github-ui/noop'
import {PlusIcon} from '@primer/octicons-react'
import {Dialog, IconButton, useConfirm} from '@primer/react'
import {type ReactNode, useCallback, useState} from 'react'
import {useRelayEnvironment} from 'react-relay'

import {LABELS} from '../../constants/labels'
import {VALUES} from '../../constants/values'
import {
  useDirtySearchQuery,
  useQueryEditState,
  useQueryMutations,
  useQueryViewEditFields,
} from '../../contexts/QueryContext'
import {useAppNavigate} from '../../hooks/use-app-navigate'
import {commitRemoveUserViewMutation} from '../../mutations/remove-user-view-mutation'
import styles from './CreateSavedView.module.css'

type Props = {
  disabled: boolean
}

/**
 * Shared create-saved-view behavior. Returns the accessible `label`, the
 * `onClick` handler (create a view, or open the max-views dialog when at the
 * limit), and the `maxViewsDialog` node to render. Used by both the expanded
 * sidebar's header `IconButton` ({@link CreateSavedView}) and the collapsed
 * submenu popover's `ActionList.GroupHeading.TrailingAction` slot, which must
 * be authored inline as a direct child of the heading for Primer's slot
 * detection to position it at the trailing end of the row.
 */
export function useCreateSavedView(disabled: boolean): {
  label: string
  onClick: () => void
  maxViewsDialog: ReactNode
} {
  const {commitUserViewCreate} = useQueryMutations()
  const {dirtyDescription, dirtyTitle} = useQueryViewEditFields()
  const dirtySearchQuery = useDirtySearchQuery()
  const {dirtyViewId, setDirtyViewId, isNewView} = useQueryEditState()
  const relayEnvironment = useRelayEnvironment()
  const confirm = useConfirm()
  const [isDialogOpen, setIsDialogOpen] = useState(false)

  const {navigateToSavedView} = useAppNavigate()

  const openMaxViewsDialog = useCallback(() => {
    setIsDialogOpen(true)
  }, [setIsDialogOpen])

  const deleteDirtyView = useCallback(() => {
    if (dirtyViewId === undefined) {
      return
    }

    const payload = {
      environment: relayEnvironment,
      input: {shortcutId: dirtyViewId},
      onError: noop,
      onCompleted: noop,
    }

    commitRemoveUserViewMutation(payload)
  }, [dirtyViewId, relayEnvironment])

  const createNewView = useCallback(async () => {
    // When clicking the create view button while the user is editing a newly created view
    if (isNewView && (dirtyTitle !== LABELS.views.defaultName || dirtySearchQuery !== '' || dirtyDescription !== '')) {
      const discardChanges = await confirm({
        title: LABELS.views.unsavedChangesTitle,
        content: LABELS.views.unsavedChangesContent,
        confirmButtonType: 'danger',
      })

      if (!discardChanges) {
        return
      }

      deleteDirtyView()
    }

    commitUserViewCreate({
      onSuccess: ({createDashboardSearchShortcut}) => {
        if (createDashboardSearchShortcut?.shortcut) {
          setDirtyViewId(createDashboardSearchShortcut.shortcut.id)
          navigateToSavedView(createDashboardSearchShortcut.shortcut.id, {isNewView: true})
        }
      },
      relayEnvironment,
    })
  }, [
    commitUserViewCreate,
    confirm,
    deleteDirtyView,
    dirtyDescription,
    dirtySearchQuery,
    dirtyTitle,
    isNewView,
    navigateToSavedView,
    relayEnvironment,
    setDirtyViewId,
  ])

  return {
    label: disabled ? LABELS.views.maxViewsTooltip : LABELS.views.createLink,
    onClick: disabled ? openMaxViewsDialog : createNewView,
    maxViewsDialog:
      disabled && isDialogOpen ? (
        <Dialog
          title={`${LABELS.views.maxViewsTooltip} (${VALUES.viewsPageSize})`}
          onClose={() => setIsDialogOpen(false)}
        >
          {LABELS.views.maxViewsDescription}
        </Dialog>
      ) : null,
  }
}

export function CreateSavedView({disabled}: Props) {
  const {label, onClick, maxViewsDialog} = useCreateSavedView(disabled)

  return (
    <>
      <IconButton
        icon={PlusIcon}
        size="small"
        variant="invisible"
        aria-label={label}
        onClick={onClick}
        className={disabled ? styles.disabled : ''}
        inactive={disabled}
      />
      {maxViewsDialog}
    </>
  )
}
